import PropTypes from 'prop-types'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue
} from '@components/ui/select'
import { cn } from 'lib/utils'

// Besides selectable options, `options` may hold two structural entries that cannot be picked:
// `{ divider: true }` draws a line, and `{ heading: 'Clients' }` starts a labelled group that
// runs until the next divider or heading.
const isStructural = (option) => typeof option === 'object' && option !== null && (option.divider || option.heading)

const toPair = (option) =>
  typeof option === 'object' ? { key: option.id ?? option.value, value: option.name || option.value } : { key: option, value: option }

const toKeyValuePairs = (options) => options.filter((option) => !isStructural(option)).map(toPair)

// Splits the options into runs: plain items, a divider, or a heading with the items under it.
export const toSections = (options) => {
  const sections = []
  let current = null
  options.forEach((option) => {
    if (isStructural(option) && option.divider) {
      sections.push({ divider: true })
      current = null
    } else if (isStructural(option)) {
      current = { heading: option.heading, items: [] }
      sections.push(current)
    } else {
      if (!current) {
        current = { items: [] }
        sections.push(current)
      }
      current.items.push(toPair(option))
    }
  })
  return sections
}

const renderItem = (option) => (
  <SelectItem key={option.key} value={String(option.key)}>
    {option.value}
  </SelectItem>
)

export const AdasSelect = ({
  value,
  onChange,
  options = [],
  disabled = false,
  className,
  placeholder = 'Select...',
  name,
  // eslint-disable-next-line no-unused-vars
  sx,
  // eslint-disable-next-line no-unused-vars
  variant,
  // eslint-disable-next-line no-unused-vars
  labelId,
  // eslint-disable-next-line no-unused-vars
  label,
  // eslint-disable-next-line no-unused-vars
  id,
  ...rest
}) => {
  const pairs = toKeyValuePairs(options)
  const handleChange = (selected) => {
    // Restore the original key type (may be numeric) before emitting
    const original = pairs.find((p) => String(p.key) === selected)
    const resolvedValue = original !== undefined ? original.key : selected
    if (onChange) onChange({ target: { name, value: resolvedValue } })
  }
  return (
    <Select value={value != null ? String(value) : undefined} onValueChange={handleChange} disabled={disabled}>
      <SelectTrigger className={cn('w-full border-custom-borderInput', className)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {toSections(options).map((section, index) =>
          section.divider ? (
            <SelectSeparator key={`divider-${index}`} />
          ) : section.heading ? (
            <SelectGroup key={`group-${index}`}>
              <SelectLabel>{section.heading}</SelectLabel>
              {section.items.map(renderItem)}
            </SelectGroup>
          ) : (
            section.items.map(renderItem)
          )
        )}
      </SelectContent>
    </Select>
  )
}

AdasSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  options: PropTypes.array,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  placeholder: PropTypes.string
}
